import { format, parseISO } from "date-fns";
import { Disclosure } from "@headlessui/react";
import { Dispatch, FC, SetStateAction } from "react";
import { RentalPrice } from "../../../api/types";
import { FormatMoney } from "../../../utils/format";

interface Props {
  offer: RentalPrice;
  setOffer: Dispatch<SetStateAction<RentalPrice | undefined>>;
}

const SummaryMobile: FC<Props> = ({ offer, setOffer }) => {
  const transmission = offer.isAutomatic ? "Automático" : "Manual";

  const imageSrc =
    offer.category.cars?.[0]?.pictures?.[0] ??
    process.env.REACT_APP_IMG_PATH + "/img/car-no-image.webp";

  return (
    <div>
      <section
        aria-labelledby="order-heading"
        className="tw-bg-gray-50 tw-px-4 tw-py-4 sm:tw-px-6 md:tw-hidden"
      >
        <Disclosure as="div" className="tw-max-w-lg tw-mx-auto">
          {({ open }) => (
            <>
              <div className="tw-flex tw-items-center tw-justify-between">
                <h2
                  id="order-heading"
                  className="tw-text-lg tw-font-medium tw-text-gray-900"
                >
                  Resumen de su renta
                </h2>
                <Disclosure.Button className="tw-font-medium tw-text-indigo-600 hover:tw-text-indigo-500">
                  {open ? (
                    <span>Ocultar resumen</span>
                  ) : (
                    <span>Ver resumen</span>
                  )}
                </Disclosure.Button>
              </div>

              <Disclosure.Panel>
                <ul className="tw-divide-y tw-divide-gray-200 tw-border-b tw-border-gray-200">
                  <li className="tw-flex tw-py-6 tw-space-x-6">
                    <img
                      src={imageSrc}
                      alt={`${offer.category.nameSpanish} - ${transmission}`}
                      className="tw-flex-none tw-w-32 tw-h-32 tw-object-center tw-object-cover tw-bg-gray-200 tw-rounded-md"
                    />
                    <div className="tw-flex tw-flex-col tw-justify-between tw-space-y-4">
                      <div className="tw-text-sm tw-font-medium tw-space-y-1">
                        <h3 className="tw-text-gray-900">
                          {offer.category.nameSpanish}
                        </h3>
                        <p className="tw-text-gray-500">{transmission}</p>

                        <p className="tw-text-gray-500">
                          Recogida:{" "}
                          {format(parseISO(offer.start), "dd MMM yyyy")}
                        </p>
                        <p className="tw-text-gray-500">
                          Entrega: {format(parseISO(offer.end), "dd MMM yyyy")}
                        </p>
                      </div>
                      <div className="tw-flex tw-space-x-4">
                        <button
                          onClick={() => setOffer(undefined)}
                          type="button"
                          className="tw-text-sm tw-font-medium tw-text-orange-600"
                        >
                          Cambiar renta
                        </button>
                      </div>
                    </div>
                  </li>
                </ul>

                <div className="tw-sticky tw-bottom-0 tw-flex-none tw-bg-gray-50 tw-border-t tw-border-gray-200 tw-px-0 tw-py-6 tw-pt-2">
                  <dl className="tw-text-sm tw-font-medium tw-text-gray-500 tw-space-y-6">
                    <div className="tw-flex tw-items-center tw-justify-between  tw-text-gray-900 tw-pt-2">
                      <dt className="tw-text-base tw-font-medium tw-text-indigo-600">
                        A pagar en Cuba
                      </dt>
                    </div>
                    <div className="tw-flex tw-justify-between">
                      <dt>Deposito</dt>
                      <dd className="tw-text-gray-900">
                        {FormatMoney(offer.cost.deposit)}
                      </dd>
                    </div>
                    <div className="tw-flex tw-justify-between">
                      <dt>Seguro</dt>
                      <dd className="tw-text-gray-900">
                        {offer.cost.insurance === 0
                          ? "Incluído"
                          : FormatMoney(offer.cost.insurance)}
                      </dd>
                    </div>
                  </dl>
                </div>
                <div className="tw-sticky tw-bottom-0 tw-flex-none tw-bg-gray-50 tw-border-t tw-border-gray-200 tw-px-0 tw-py-6 tw-pt-2">
                  <dl className="tw-text-sm tw-font-medium tw-text-gray-500 tw-mt-4 tw-space-y-6">
                    <div className="tw-flex tw-items-center tw-justify-between  tw-text-gray-900 tw-pt-2">
                      <dt className="tw-text-base tw-font-medium tw-text-indigo-600">
                        Desglose del precio
                      </dt>
                    </div>
                    <div className="tw-flex tw-justify-between">
                      <dt>Dias de renta</dt>
                      <dd className="tw-text-gray-900">{offer.days}</dd>
                    </div>
                    <div className="tw-flex tw-justify-between">
                      <dt>Costo Diario</dt>
                      <dd className="tw-text-gray-900">
                        {FormatMoney(offer.cost.dailyPrice)}
                      </dd>
                    </div>
                    <div className="tw-flex tw-justify-between tw-border-y tw-border-gray-200 tw-pt-6 tw-pb-6">
                      <dt>Subtotal</dt>
                      <dd className="tw-text-gray-900">
                        {FormatMoney(offer.days * offer.cost.dailyPrice)}
                      </dd>
                    </div>
                    {offer.cost.pickupFee > 0 && (
                      <div className="tw-flex tw-justify-between ">
                        <dt>Cargo por Recogida</dt>
                        <dd className="tw-text-gray-900">
                          {FormatMoney(offer.cost.pickupFee)}
                        </dd>
                      </div>
                    )}
                    {offer.cost.gasFee > 0 && (
                      <div className="tw-flex tw-justify-between">
                        <dt>Cargo por combustible</dt>
                        <dd className="tw-text-gray-900">
                          {FormatMoney(offer.cost.gasFee)}
                        </dd>
                      </div>
                    )}
                    <div className="tw-flex tw-justify-between">
                      <dt>Tarifa pago por tarjeta de crédito</dt>
                      <dd className="tw-text-gray-900">
                        {FormatMoney(offer.cost.creditCardFee)}
                      </dd>
                    </div>
                  </dl>
                </div>
              </Disclosure.Panel>

              <p className="tw-flex tw-items-center tw-justify-between tw-text-sm tw-font-medium tw-text-gray-900 tw-border-t tw-border-gray-200 tw-pt-6 tw-mt-2">
                <span className="tw-text-base">Total</span>
                <span className="tw-text-base">
                  {FormatMoney(offer.cost.sale)}
                </span>
              </p>
            </>
          )}
        </Disclosure>
      </section>
    </div>
  );
};

export default SummaryMobile;
